import { useEffect, useState } from 'react';
import '../page-list/PageList.css';
import '../../components/fileImports/ImportQueuePage.css';
import styles from '../clients/ClientList.module.scss';
import { getDateFromZulu } from '../helpers/getDateFromZulu';
import Spinner from '../ui/spinners/Spinner';
import '../page-list/PageList.css';
import TextRangeDatePicker from '../helpers/TextRangeDatePicker';
import SearchComponent from '../helpers/search/SearchComponent';
import { subDays, format } from 'date-fns';
import { SortDirections } from '../../hooks/useTableSort';
import PaginationComponent from '../helpers/PaginationComponent';
import { FilterModal, renderFilterIcon } from '../helpers/FilterComponent';
import EmptyTableRow from '../ui/no_data_indicators/EmptyTableRow';
import { getMessagesHistories } from '../../services/clientService';
import { useAuthContext } from '../../store/AuthContext';
import Toast from '../ui/toasts/Toast';
import { ROUTE_BUILDERS } from '../../constants/routes';
import { useAdminAudienceEnabled } from '../../hooks/useAdminAudienceEnabled';
import { useNavigate } from 'react-router-dom';
import { downloadReport } from '../individual/components/NotificationHistoryReport';
import { getNotificationCategories } from '../../services/messageService';
import { fetchPopulationGroups } from '../../services/populationGroupService';
import { getText } from '../../i18n';

const MessagesHistory = () => {
    const navigate = useNavigate();
    const defaultStartDate = subDays(new Date(), 7);
    const defaultEndDate = new Date();
    const pageSize = 20;

    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [messageHistories, setMessageHistories] = useState([]);
    const [baseFilteredData, setBaseFilteredData] = useState([]);
    const [notificationCategories, setNotificationCategories] = useState([]);
    const [
        selectedNotificationCategoryIds,
        setSelectedNotificationCategoryIds,
    ] = useState([]);
    const [populationGroups, setPopulationGroups] = useState([]);
    const [selectedPopulationGroupIds, setSelectedPopulationGroupIds] =
        useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'sentAt',
        direction: SortDirections.DESCENDING,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [modalSettings, setModalSettings] = useState({
        x: 0,
        y: 0,
        reviewedBy: '',
        reviewedAt: '',
    });
    const [modalVisible, setModalVisible] = useState(false);

    const { user } = useAuthContext();
    const { isAdminAudienceEnabled } = useAdminAudienceEnabled();
    const canViewAdminAudience =
        isAdminAudienceEnabled && Boolean(user?.jwt?.df);
    const canViewAudienceColumn =
        isAdminAudienceEnabled && !Boolean(user?.jwt?.df);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            loadMessageHistories();
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [
        startDate,
        endDate,
        searchTerm,
        sortConfig,
        currentPage,
        selectedNotificationCategoryIds,
        selectedPopulationGroupIds,
    ]);

    useEffect(() => {
        loadFilters();
    }, []);

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        if (column === 'notificationCategoryIds') {
            const newNotificationCategoryIds = notificationCategories
                .map(category => category.id)
                .filter(id =>
                    selectedOptions.includes(
                        notificationCategories.find(
                            category => category.id === id,
                        )?.name,
                    ),
                );

            setSelectedNotificationCategoryIds(newNotificationCategoryIds);
            return;
        }

        if (column === 'populationGroupIds') {
            const newPopulationGroupIds = populationGroups
                .map(category => category.id)
                .filter(id =>
                    selectedOptions.includes(
                        populationGroups.find(category => category.id === id)
                            ?.name,
                    ),
                );

            setSelectedPopulationGroupIds(newPopulationGroupIds);
            return;
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedNotificationCategoryIds([]);
        setSelectedPopulationGroupIds([]);
    };

    const handleSearch = searchBy => {
        setSearchTerm(searchBy);
    };

    const loadMessageHistories = async () => {
        try {
            setIsLoading(true);

            const fetchedMessageHistories = await getMessagesHistories(
                format(startDate, 'yyyy-MM-dd'),
                format(endDate, 'yyyy-MM-dd'),
                searchTerm,
                sortConfig.key,
                sortConfig.direction,
                currentPage,
                pageSize,
                selectedNotificationCategoryIds,
                selectedPopulationGroupIds,
            );

            const formattedMessageHistories =
                fetchedMessageHistories.viewModels;

            setMessageHistories(formattedMessageHistories);
            setBaseFilteredData(formattedMessageHistories);
            setTotalPages(Math.ceil(fetchedMessageHistories.pageTotal));
        } catch (error) {
            if (error.name === 'AbortError') {
                return null;
            }
            console.error('Failed to fetch message history', error);
            Toast.error(getText('msg_35d248aa0136'));
        } finally {
            setIsLoading(false);
        }
    };

    const loadFilters = async () => {
        try {
            setIsLoading(true);

            const fetchedNotificationCategories =
                await getNotificationCategories();

            setNotificationCategories(fetchedNotificationCategories);

            const fetchedPopulationGroups = await fetchPopulationGroups();
            setPopulationGroups(fetchedPopulationGroups);
        } catch (error) {
            if (error.name === 'AbortError') {
                return null;
            }
            console.error('Failed to fetch notification categories', error);
            Toast.error(getText('msg_c2b394c6aa8b'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSort = key => {
        setSortConfig(prevConfig => ({
            key,
            direction:
                prevConfig.key === key &&
                prevConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        }));
    };

    const handleSortName = () => handleSort('name');
    const handleSortDisplayName = () => handleSort('displayName');
    const handleSortSentAt = () => handleSort('sentAt');

    const downloadIndividualReport = async () => {
        try {
            setIsLoading(true);
            downloadReport(
                startDate,
                endDate,
                searchTerm,
                sortConfig.key,
                sortConfig.direction,
            );
        } catch (error) {
            console.error('Error downloading report', error);
            Toast.error(getText('msg_821613944fc1'));
        } finally {
            setIsLoading(false);
        }
    };

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex align-items-center mb-4 justify-content-space-between">
                        <div className="float-start d-flex">
                            <div>
                                <p className="page-title card-title ms-4 me-5">
                                    {getText('msg_565775f39772')}
                                </p>
                                <p
                                    className="ing-portals-subtitle mt-1"
                                    style={{ marginTop: '10px' }}
                                >
                                    {getText('msg_c7d691828d40')}
                                </p>
                                <p
                                    className="ing-portals-subtitle mt-1"
                                    style={{ marginTop: '10px' }}
                                >
                                    {getText('msg_df8fabd9c9e2')}
                                </p>
                            </div>
                        </div>

                        <div
                            className="d-flex"
                            style={{ paddingRight: '24px' }}
                        >
                            {canViewAdminAudience
                                ? baseFilteredData[0]?.dataFilter
                                : null}
                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>
                            <span
                                type="button"
                                onClick={() => downloadIndividualReport()}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-cloud-download me-2" />
                                {getText('msg_a479c9c34e87')}
                            </span>
                        </div>
                    </div>

                    <div className="page-title card-title ms-4 me-5">
                        <TextRangeDatePicker
                            startDate={startDate}
                            endDate={endDate}
                            setStartDate={setStartDate}
                            setEndDate={setEndDate}
                        />
                    </div>

                    <div className="page-title card-title ms-4 me-5 mb-5">
                        <div className={styles.searchWrapper}>
                            <SearchComponent
                                onSearch={event => handleSearch(event)}
                                placeholder={getText('msg_bce06414177f')}
                                initialValue={searchTerm}
                                autoFocus={true}
                            />
                        </div>
                    </div>

                    <div
                        className="table-responsive-xxl w-100"
                        style={{
                            width: '100%',
                            overflowX: 'auto',
                            position: 'relative',
                            height: 'fit-content',
                        }}
                    >
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th style={{ paddingLeft: '24px' }}>
                                        <span className="table-column-header-text">
                                            {getText('msg_709a23220f2c')}
                                        </span>
                                        <span
                                            onClick={handleSortName}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'name' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_8d6b34815ba4')}
                                        </span>
                                        <span
                                            onClick={handleSortDisplayName}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'displayName' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_35f49dcfbfb2')}
                                        </span>
                                        <span
                                            onClick={handleSortSentAt}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'sentAt' &&
                                                        sortConfig.direction ===
                                                            SortDirections.ASCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_a3c686e711e4')}
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal(
                                                    'notificationCategoryIds',
                                                );
                                            }}
                                            className={`mx-2 ${styles.cursorPointer}`}
                                        >
                                            {renderFilterIcon(
                                                selectedNotificationCategoryIds?.length >
                                                    0,
                                            )}
                                        </span>
                                        {activeModal ===
                                            'notificationCategoryIds' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={notificationCategories.map(
                                                    category => category.name,
                                                )}
                                                selectedOptions={
                                                    notificationCategories
                                                        .filter(category =>
                                                            selectedNotificationCategoryIds.includes(
                                                                category.id,
                                                            ),
                                                        )
                                                        .map(
                                                            category =>
                                                                category.name,
                                                        ) || []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    {canViewAudienceColumn && (
                                        <th>
                                            <span className="table-column-header-text">
                                                {getText('msg_51d9934555e2')}
                                            </span>
                                        </th>
                                    )}
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_e744dd6b3c48')}
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('populationGroupIds');
                                            }}
                                            className={`mx-2 ${styles.cursorPointer}`}
                                        >
                                            {renderFilterIcon(
                                                selectedPopulationGroupIds?.length >
                                                    0,
                                            )}
                                        </span>
                                        {activeModal ===
                                            'populationGroupIds' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={populationGroups.map(
                                                    category => category.name,
                                                )}
                                                selectedOptions={
                                                    populationGroups
                                                        .filter(category =>
                                                            selectedPopulationGroupIds.includes(
                                                                category.id,
                                                            ),
                                                        )
                                                        .map(
                                                            category =>
                                                                category.name,
                                                        ) || []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {messageHistories == undefined ||
                                messageHistories.length <= 0 ? (
                                    <EmptyTableRow
                                        colSpan={
                                            canViewAudienceColumn ? 6 : 5
                                        }
                                        title={getText('msg_a8dd2b928523')}
                                        description={getText(
                                            'msg_0de38290325d',
                                        )}
                                    />
                                ) : (
                                    baseFilteredData.map(
                                        (messageHistoryRecord, index) => (
                                            <tr key={index}>
                                                <td
                                                    style={{
                                                        paddingLeft: '24px',
                                                    }}
                                                    className="titleCell"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() =>
                                                        navigate(
                                                            ROUTE_BUILDERS.messagesHistoryDetails(
                                                                messageHistoryRecord.id,
                                                            ),
                                                        )
                                                    }
                                                    onKeyDown={e => {
                                                        if (
                                                            e.key === 'Enter' ||
                                                            e.key === ' '
                                                        ) {
                                                            navigate(
                                                                ROUTE_BUILDERS.messagesHistoryDetails(
                                                                    messageHistoryRecord.id,
                                                                ),
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {messageHistoryRecord.name}
                                                </td>

                                                <td className="subtitleCell">
                                                    {
                                                        messageHistoryRecord.displayName
                                                    }
                                                </td>
                                                <td className="subtitleCell">
                                                    {getDateFromZulu(
                                                        messageHistoryRecord.sentAt,
                                                    )}
                                                </td>
                                                <td className="subtitleCell">
                                                    {
                                                        messageHistoryRecord.notificationCategory
                                                    }
                                                </td>
                                                {canViewAudienceColumn && (
                                                    <td className="subtitleCell">
                                                        {
                                                            messageHistoryRecord.dataFilter
                                                        }
                                                    </td>
                                                )}
                                                <td className="item-page-changed-by">
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            flexDirection:
                                                                'row',
                                                            alignItems:
                                                                'flex-start',
                                                            gap: '6px',
                                                            maxWidth: '300px',
                                                        }}
                                                    >
                                                        {messageHistoryRecord.populationGroups.map(
                                                            (
                                                                groupID,
                                                                index,
                                                            ) => (
                                                                <span className="badge-base badge-population-group">
                                                                    {groupID}
                                                                </span>
                                                            ),
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ),
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                    {totalPages > 0 && (
                        <div className="mt-auto">
                            <PaginationComponent
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MessagesHistory;
